import React, { useEffect, useMemo, useRef } from 'react';
import { useRouter } from 'next/router';

import { Icon, Spinner } from 'components';
import { Input } from 'components/form';
import { CompanyCard } from 'components/ComponentV2/CompanyCard/CompanyCard.component';
import { ButtonV3 } from 'components/ComponentV2';
import StudentSocietyFilters from 'components/StudentSocietyFilters';
import { useStudentSocieties } from 'hooks/useStudentSocieties';
import { useVisibleOnIntersect } from 'hooks/useVisibleOnIntersect';
import { parseResponse } from 'lib/utils/parser';
import { UniversityAttributes } from 'lib/models/university';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useAuth } from 'lib/providers/AuthProvider';

import styles from './ExploreCommunities.module.scss';

interface ExploreCommunitiesProps {
  goBack: () => void;
}

export const ExploreCommunities: React.FC<ExploreCommunitiesProps> = (
  props
) => {
  const { goBack } = props;
  const router = useRouter();
  const { isRecruiter } = useAuth();

  const useBottomRef = useRef<HTMLDivElement | null>(null);

  const {
    societies,
    isLoading,
    fetchMoreSocieties,
    hasMoreRecords,
    search,
    setSearch,
    minimumMembersOptions,
    minimumEventsOptions,
    sinceMonthsOptions,
    filters,
    partialFilters,
    onChangeFilters,
    partialCount,
    partialLoading,
  } = useStudentSocieties();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useVisibleOnIntersect({
    elRef: useBottomRef,
    threshold: 0,
    onIntersect: isLoading ? undefined : fetchMoreSocieties,
    rootMargin: '250px',
  });

  const goToCommunityPage = (slug: string) => () => {
    router.push(`${PAGE_ROUTES.STUDENT_SOCIETIES}/${slug}/detail`);
  };

  const renderSocieties = useMemo(() => {
    return (
      <div className={styles.cardContainer}>
        {societies.map((society) => {
          const {
            id,
            icon_url,
            slug,
            name,
            is_huzzle_verified,
            is_verified,
            university: universityResponse,
          } = society;
          const university =
            parseResponse<UniversityAttributes>(universityResponse);

          return (
            <CompanyCard
              key={id}
              name={name}
              icon={icon_url}
              title={name}
              caption={university?.name}
              isHuzzleVerified={is_huzzle_verified}
              isVerified={is_verified}
              onSelect={goToCommunityPage(slug)}
              className={styles.communityCard}
            >
              <ButtonV3
                color="secondary"
                size="small"
                onClick={goToCommunityPage(slug)}
              >
                View
              </ButtonV3>
            </CompanyCard>
          );
        })}
      </div>
    );
  }, [societies]);

  const searchBox = useMemo(() => {
    return (
      <Input
        placeholder="Search"
        startIcon="icon_search"
        inputClassName={styles.searchBar}
        startIconClassName={styles.searchBarStartIcon}
        value={search}
        onChange={(event) => setSearch(event.target.value)}
      />
    );
  }, [search]);

  return (
    <div className={styles.ExploreCommunities}>
      <div className={styles.ExploreCommunitiesHeader}>
        <div className={styles.breadcrumb} onClick={goBack}>
          <Icon size="large" iconName="chevron-left" />
          <p>Partnerships</p>
        </div>
        <p className={styles.pageTitle}>Explore communities</p>
        <div className={styles.ExploreCommunitiesFilter}>
          <StudentSocietyFilters
            className={styles.filters}
            headerScrolled={false}
            isFilterClicked={false}
            isRecruiter={isRecruiter}
            searchBox={searchBox}
            minimumMembersOptions={minimumMembersOptions}
            minimumEventsOptions={minimumEventsOptions}
            sinceMonthsOptions={sinceMonthsOptions}
            filters={filters}
            partialFilters={partialFilters}
            onChangeFilters={onChangeFilters}
            partialCount={partialCount}
            partialLoading={partialLoading}
          />
        </div>
      </div>
      <div className={styles.ExploreCommunitiesContent}>
        {isLoading ? (
          <Spinner size="medium" wrapper />
        ) : societies.length ? (
          renderSocieties
        ) : (
          <div className={styles.ExploreCommunitiesContentEmpty}>
            <p className={styles.emoji}>🙁</p>
            <p className={styles.emptyText}>No Results</p>
          </div>
        )}
        <div ref={useBottomRef} className={styles.bottomElement}>
          {hasMoreRecords ? (
            <Spinner size="medium" wrapperClassname={styles.spinner} />
          ) : null}
        </div>
      </div>
    </div>
  );
};
